import { useEffect, useMemo, useRef, useState } from 'react'
import { useUIStore } from '@/stores/uiStore'
import { useSequencerStore, selectCurrentBuses } from '@/stores/sequencerStore'
import { getUserBus } from '@/audio/busRegistry'
import { getFftSnapshot } from '@/audio/meters'
import { hasAnalyzerId } from '@/audio/tone/fftAnalyzer'

/**
 * Voxengo-SPAN-style real-time spectrum view.
 *
 * Resolves its data source via
 *   `uiStore.selectedPlugin → busRegistry.getUserBus(busId) →
 *    bus.getPluginHandle(effectId) → hasAnalyzerId(handle) →
 *    getFftSnapshot(analyzerId)`
 * on every RAF tick. If any step in that chain fails (bus was
 * deleted, effect was removed, plugin not loaded yet) we fall
 * back to the "no plugin open" empty state — the same message
 * `PluginPane` renders when nothing is selected.
 *
 * The view controls (channel mode, slope tilt, peak-hold, dB range,
 * FFT size, smoothing) are persisted on the BusEffect.params blob
 * so closing + reopening the tab or the whole app restores them.
 * Only `fftSize` + `smoothing` touch the DSP via `updateBusEffect` →
 * `applyEffectParams` → `plugin.setParam`; the other four (window,
 * channelMode, slopeDbPerOct, peakHold, dbRange) are view-only.
 *
 * Rendering pattern follows `InputMeter.tsx` — RAF loop in a ref,
 * scratch buffers pulled from `meters.ts` so no per-frame
 * allocation. The canvas is `devicePixelRatio`-scaled (matching
 * `WaveformViewer.tsx`) so the traces stay crisp on retina.
 */

interface DbRangeSpec {
  label: string
  minDb: number
  maxDb: number
}
const DB_RANGES: Record<number, DbRangeSpec> = {
  0: { label: '-60 → 0 dB', minDb: -60, maxDb: 0 },
  1: { label: '-90 → 0 dB', minDb: -90, maxDb: 0 },
  2: { label: '-120 → 0 dB', minDb: -120, maxDb: 0 },
}

const CHANNEL_MODE_LABELS: Record<number, string> = {
  0: 'Stereo',
  1: 'Mono (L+R)',
  2: 'Mid / Side',
  3: 'Left only',
  4: 'Right only',
}

const MIN_FREQ_HZ = 20
const MAX_FREQ_HZ = 20_000
const LOG_MIN = Math.log10(MIN_FREQ_HZ)
const LOG_MAX = Math.log10(MAX_FREQ_HZ)

/** 1-decade ticks for the grid + axis labels. */
const FREQ_GRID_HZ = [
  20, 30, 50, 100, 200, 300, 500, 1000, 2000, 3000, 5000, 10_000, 20_000,
] as const
const FREQ_LABEL_HZ = new Set<number>([20, 100, 1000, 10_000, 20_000])

function formatHz(hz: number): string {
  if (hz >= 1000) return `${hz / 1000}k`
  return String(hz)
}

interface SpectrumAnalyzerProps {
  /** When true, render at full-viewport height inside the
   *  `FullScreenModal`. Otherwise the embedded 32rem minimum is used
   *  so the analyser still has room in the default tab slot. */
  fullScreen?: boolean
}

export function SpectrumAnalyzer({
  fullScreen = false,
}: SpectrumAnalyzerProps = {}) {
  const selected = useUIStore(s => s.selectedPlugin)
  const setSelectedPlugin = useUIStore(s => s.setSelectedPlugin)
  const setMainPaneTab = useUIStore(s => s.setMainPaneTab)
  const buses = useSequencerStore(selectCurrentBuses)
  const updateBusEffect = useSequencerStore(s => s.updateBusEffect)

  const bus = useMemo(
    () => (selected ? buses.find(b => b.id === selected.busId) : undefined),
    [buses, selected],
  )
  const effect = useMemo(
    () =>
      bus?.effects?.find(
        e => e.id === selected?.effectId && e.pluginId === 'fft-analyzer',
      ),
    [bus, selected],
  )

  // Resolve the live analyzer id. We poll this imperatively inside
  // the RAF loop because the plugin handle is on the bus registry,
  // not in React state — re-creating the selector per render would
  // churn. Keeping it in a ref + re-resolving once per tab open
  // matches the InputMeter pattern.
  const analyzerIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!selected) {
      analyzerIdRef.current = null
      return
    }
    const liveBus = getUserBus(selected.busId)
    const handle = liveBus?.getPluginHandle(selected.effectId) ?? null
    analyzerIdRef.current =
      handle && hasAnalyzerId(handle) ? handle.getAnalyzerId() : null
  }, [selected])

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const rafRef = useRef<number | null>(null)
  // Peak-envelope buffer — allocated lazily once we know the bin
  // count so the RAF loop never malloc-s. Re-sized on bin-count
  // change below.
  const peakHoldRef = useRef<Float32Array | null>(null)
  const [, forceResize] = useState(0)

  // Read view-only params off the BusEffect.params blob. Defaults
  // mirror the plugin's `defaultPreset` so the UI starts in a sane
  // state even on a fresh analyzer instance.
  const params = effect?.params ?? {}
  const dbRangeId = Math.round(Number(params.dbRange ?? 1))
  const dbRange = DB_RANGES[dbRangeId] ?? DB_RANGES[1]!
  const slopeDbPerOct = Number(params.slopeDbPerOct ?? 4.5)
  const peakHoldOn = Boolean(Number(params.peakHold ?? 1))
  const channelMode = Math.round(Number(params.channelMode ?? 0))
  const fftSize = Math.round(Number(params.fftSize ?? 2048))
  const smoothing = Number(params.smoothing ?? 0.3)

  const setParam = (key: string, value: number) => {
    if (!selected) return
    updateBusEffect(selected.busId, selected.effectId, {
      params: { [key]: value },
    })
  }

  // Resize handler — redraw the canvas's backing store when the
  // container changes size so the spectrum stays sharp on window
  // resize / viewport changes.
  useEffect(() => {
    if (!canvasRef.current) return
    const canvas = canvasRef.current
    const observer = new ResizeObserver(() => forceResize(n => n + 1))
    observer.observe(canvas)
    return () => observer.disconnect()
  }, [])

  // Main RAF loop. Reads the latest FFT snapshot from the meters
  // registry and paints the canvas. Bails cleanly if the analyzer
  // id isn't resolved yet (engine booting) or the selection was
  // cleared while the loop was mid-frame.
  useEffect(() => {
    if (!canvasRef.current) return
    const canvas = canvasRef.current

    const loop = () => {
      rafRef.current = requestAnimationFrame(loop)
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const dpr = window.devicePixelRatio ?? 1
      const width = canvas.clientWidth
      const height = canvas.clientHeight
      if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
        canvas.width = Math.floor(width * dpr)
        canvas.height = Math.floor(height * dpr)
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      drawBackground(ctx, width, height, dbRange)

      const id = analyzerIdRef.current
      if (!id) return
      const snap = getFftSnapshot(id)
      if (!snap) return

      // Peak-hold buffer sized to bin count; resize when FFT size
      // changes. Decay the held values by a fixed amount per frame
      // (~3 dB/sec @ 60fps = 0.05 dB/frame).
      if (
        !peakHoldRef.current ||
        peakHoldRef.current.length !== snap.binCount
      ) {
        peakHoldRef.current = new Float32Array(snap.binCount)
        peakHoldRef.current.fill(dbRange.minDb)
      }
      const peaks = peakHoldRef.current
      if (peakHoldOn) {
        for (let i = 0; i < peaks.length; i++) {
          const live = snap.left[i] ?? dbRange.minDb
          const liveR = snap.right[i] ?? dbRange.minDb
          const max = Math.max(live, liveR)
          peaks[i] = Math.max(peaks[i]! - 0.05, max)
        }
      }

      drawTraces(ctx, width, height, snap, {
        channelMode,
        slopeDbPerOct,
        dbRange,
        peakHoldOn,
        peakHold: peaks,
      })
    }
    rafRef.current = requestAnimationFrame(loop)
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [
    channelMode,
    slopeDbPerOct,
    dbRange,
    peakHoldOn,
    // Intentionally omit `forceResize` counter — the RAF loop reads
    // canvas.clientWidth each frame so container resizes take
    // effect without restarting the loop.
  ])

  if (!selected || !bus || !effect) {
    return (
      <div className="flex min-h-[20rem] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-brd-subtle bg-surface-sunken/60 px-6 py-12 text-center">
        <p className="text-sm font-medium text-content-primary">
          Plugin not found
        </p>
        <p className="max-w-md text-xs text-content-secondary">
          The analyzer was removed or the bus it lived on is gone. Open a new
          one from a bus FX chain.
        </p>
      </div>
    )
  }

  return (
    <div
      className={`flex flex-col gap-3 rounded-lg border border-brd-subtle bg-surface-sunken p-3 ${
        fullScreen ? 'h-full' : 'flex-1 min-h-[20rem]'
      }`}
    >
      {/* Header: title + channel/view controls. */}
      <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-content-secondary">
        <span className="text-content-primary font-semibold">
          FFT Analyzer · {bus.name ?? bus.id}
        </span>
        <span className="flex-1" />
        <label className="flex items-center gap-1">
          <span>FFT</span>
          <select
            value={fftSize}
            onChange={e => setParam('fftSize', Number(e.target.value))}
            className="bg-surface-base border border-brd-subtle rounded px-1 py-0.5 text-content-primary tabular-nums"
          >
            {[512, 1024, 2048, 4096, 8192].map(n => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1">
          <span>Mode</span>
          <select
            value={channelMode}
            onChange={e => setParam('channelMode', Number(e.target.value))}
            className="bg-surface-base border border-brd-subtle rounded px-1 py-0.5 text-content-primary"
          >
            {Object.entries(CHANNEL_MODE_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1">
          <span>Slope</span>
          <input
            type="range"
            min={0}
            max={6}
            step={0.5}
            value={slopeDbPerOct}
            onChange={e => setParam('slopeDbPerOct', Number(e.target.value))}
            className="w-24 accent-[color:var(--accent)]"
            aria-label="Slope dB per octave"
          />
          <span className="tabular-nums text-content-primary w-10 text-right">
            {slopeDbPerOct.toFixed(1)}
          </span>
        </label>
        <label className="flex items-center gap-1">
          <span>Smooth</span>
          <input
            type="range"
            min={0}
            max={0.95}
            step={0.01}
            value={smoothing}
            onChange={e => setParam('smoothing', Number(e.target.value))}
            className="w-24 accent-[color:var(--accent)]"
            aria-label="Smoothing"
          />
          <span className="tabular-nums text-content-primary w-10 text-right">
            {smoothing.toFixed(2)}
          </span>
        </label>
        <label className="flex items-center gap-1">
          <span>Range</span>
          <select
            value={dbRangeId}
            onChange={e => setParam('dbRange', Number(e.target.value))}
            className="bg-surface-base border border-brd-subtle rounded px-1 py-0.5 text-content-primary"
          >
            {Object.entries(DB_RANGES).map(([v, spec]) => (
              <option key={v} value={v}>
                {spec.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => setParam('peakHold', peakHoldOn ? 0 : 1)}
          aria-pressed={peakHoldOn}
          className={`px-2 py-0.5 rounded border text-[10px] ${
            peakHoldOn
              ? 'bg-accent text-[color:var(--accent-ink)] border-transparent'
              : 'bg-surface-base border-brd-subtle text-content-secondary'
          }`}
        >
          Peak hold
        </button>
        <button
          type="button"
          onClick={() => {
            setSelectedPlugin(null)
            setMainPaneTab('grid')
          }}
          title="Close analyzer"
          className="px-2 py-0.5 rounded border border-brd-subtle bg-surface-base text-content-tertiary hover:text-accent"
        >
          Close
        </button>
      </div>

      <div className="relative flex-1 min-h-[20rem]">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full rounded bg-black"
          role="img"
          aria-label="Frequency spectrum"
        />
      </div>
    </div>
  )
}

/** Paint background grid + axis labels. Runs first each frame so the
 *  traces draw on top. */
function drawBackground(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  dbRange: DbRangeSpec,
): void {
  ctx.fillStyle = '#000'
  ctx.fillRect(0, 0, width, height)
  ctx.strokeStyle = 'rgba(255,255,255,0.08)'
  ctx.lineWidth = 1

  // Log-frequency vertical grid lines.
  ctx.beginPath()
  for (const hz of FREQ_GRID_HZ) {
    const x = hzToX(hz, width)
    ctx.moveTo(x + 0.5, 0)
    ctx.lineTo(x + 0.5, height - 14)
  }
  ctx.stroke()

  // Horizontal dB grid every 6 dB.
  ctx.beginPath()
  for (let db = dbRange.maxDb; db >= dbRange.minDb; db -= 6) {
    const y = dbToY(db, height - 14, dbRange)
    ctx.moveTo(0, y + 0.5)
    ctx.lineTo(width, y + 0.5)
  }
  ctx.stroke()

  // Axis labels.
  ctx.fillStyle = 'rgba(255,255,255,0.45)'
  ctx.font =
    '9px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
  ctx.textBaseline = 'top'
  for (const hz of FREQ_GRID_HZ) {
    if (!FREQ_LABEL_HZ.has(hz)) continue
    const x = hzToX(hz, width)
    ctx.textAlign = 'center'
    ctx.fillText(formatHz(hz), x, height - 12)
  }
  ctx.textAlign = 'left'
  for (let db = dbRange.maxDb; db >= dbRange.minDb; db -= 12) {
    const y = dbToY(db, height - 14, dbRange)
    ctx.fillText(`${db}`, 4, y + 2)
  }
}

interface TraceOpts {
  channelMode: number
  slopeDbPerOct: number
  dbRange: DbRangeSpec
  peakHoldOn: boolean
  peakHold: Float32Array
}

/** Paint the live trace(s) + optional peak-hold envelope. Converts
 *  bin index → frequency → log x; bin dB → tilted dB → y. */
function drawTraces(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  snap: {
    left: Float32Array
    right: Float32Array
    sampleRate: number
    binCount: number
  },
  opts: TraceOpts,
): void {
  const { channelMode, slopeDbPerOct, dbRange, peakHoldOn, peakHold } = opts
  const plotHeight = height - 14
  const nyquist = snap.sampleRate / 2
  const binStep = nyquist / snap.binCount

  // Channel mode decides which trace(s) to draw.
  // Colours borrow the SPAN convention (cyan = L, magenta = R) but
  // draw from the accent for the mono / mid-side cases so themed
  // palettes still look intentional.
  const traces: Array<{ color: string; data: (i: number) => number }> = []
  if (channelMode === 0) {
    // stereo overlay
    traces.push(
      { color: 'rgba(100,220,255,0.85)', data: i => snap.left[i] ?? -120 },
      { color: 'rgba(255,100,200,0.85)', data: i => snap.right[i] ?? -120 },
    )
  } else if (channelMode === 1) {
    // mono sum — average in linear amplitude, back to dB
    traces.push({
      color: 'rgba(255,220,120,0.9)',
      data: i => {
        const l = Math.pow(10, (snap.left[i] ?? -120) / 20)
        const r = Math.pow(10, (snap.right[i] ?? -120) / 20)
        const avg = (l + r) / 2
        return avg > 0 ? 20 * Math.log10(avg) : -120
      },
    })
  } else if (channelMode === 2) {
    // mid / side — each as a separate trace
    traces.push(
      {
        color: 'rgba(100,220,255,0.85)',
        data: i => {
          const l = Math.pow(10, (snap.left[i] ?? -120) / 20)
          const r = Math.pow(10, (snap.right[i] ?? -120) / 20)
          const mid = (l + r) / 2
          return mid > 0 ? 20 * Math.log10(mid) : -120
        },
      },
      {
        color: 'rgba(255,100,200,0.85)',
        data: i => {
          const l = Math.pow(10, (snap.left[i] ?? -120) / 20)
          const r = Math.pow(10, (snap.right[i] ?? -120) / 20)
          const side = Math.abs(l - r) / 2
          return side > 0 ? 20 * Math.log10(side) : -120
        },
      },
    )
  } else if (channelMode === 3) {
    traces.push({
      color: 'rgba(100,220,255,0.9)',
      data: i => snap.left[i] ?? -120,
    })
  } else if (channelMode === 4) {
    traces.push({
      color: 'rgba(255,100,200,0.9)',
      data: i => snap.right[i] ?? -120,
    })
  }

  // Peak-hold trace drawn first so live sits on top.
  if (peakHoldOn) {
    ctx.strokeStyle = 'rgba(255,255,255,0.5)'
    ctx.lineWidth = 1
    ctx.beginPath()
    drawPath(
      ctx,
      width,
      plotHeight,
      snap,
      binStep,
      peakHold,
      slopeDbPerOct,
      dbRange,
    )
    ctx.stroke()
  }

  for (const trace of traces) {
    ctx.strokeStyle = trace.color
    ctx.lineWidth = 1.5
    ctx.beginPath()
    drawPathFromFn(
      ctx,
      width,
      plotHeight,
      snap,
      binStep,
      trace.data,
      slopeDbPerOct,
      dbRange,
    )
    ctx.stroke()
  }
}

function drawPath(
  ctx: CanvasRenderingContext2D,
  width: number,
  plotHeight: number,
  snap: { binCount: number },
  binStep: number,
  data: Float32Array,
  slopeDbPerOct: number,
  dbRange: DbRangeSpec,
): void {
  drawPathFromFn(
    ctx,
    width,
    plotHeight,
    snap,
    binStep,
    i => data[i] ?? -120,
    slopeDbPerOct,
    dbRange,
  )
}

/** Shared path walker. Skips bins below MIN_FREQ_HZ (the log scale
 *  isn't defined there) and stops at MAX_FREQ_HZ so the trace
 *  doesn't hug the right edge. */
function drawPathFromFn(
  ctx: CanvasRenderingContext2D,
  width: number,
  plotHeight: number,
  snap: { binCount: number },
  binStep: number,
  data: (i: number) => number,
  slopeDbPerOct: number,
  dbRange: DbRangeSpec,
): void {
  let moved = false
  for (let i = 1; i < snap.binCount; i++) {
    const hz = i * binStep
    if (hz < MIN_FREQ_HZ) continue
    if (hz > MAX_FREQ_HZ) break
    const rawDb = data(i)
    // Pink-noise slope compensation: boost higher frequencies so
    // a spectrally-flat noise source reads as flat on screen.
    const tiltedDb = rawDb + slopeDbPerOct * Math.log2(hz / 1000)
    const x = hzToX(hz, width)
    const y = dbToY(tiltedDb, plotHeight, dbRange)
    if (!moved) {
      ctx.moveTo(x, y)
      moved = true
    } else {
      ctx.lineTo(x, y)
    }
  }
}

function hzToX(hz: number, width: number): number {
  const t = (Math.log10(hz) - LOG_MIN) / (LOG_MAX - LOG_MIN)
  return t * width
}

function dbToY(db: number, plotHeight: number, range: DbRangeSpec): number {
  const t = (range.maxDb - db) / (range.maxDb - range.minDb)
  return Math.max(0, Math.min(plotHeight, t * plotHeight))
}
